import React from 'react';
import { styled } from '@linaria/react';

import BezierControl from '@/components/Controls/BezierControl';
import SliderControl from '@/components/Controls/SliderControl';
import ColorsControl from './ColorsControl';
import AngleControl from './AngleControl';
import ModeControl from './ModeControl';

import {
  MIN_PRECISION,
  MAX_PRECISION,
  CURVE_PRESETS,
  ColorWithId,
  ColorMode,
  EasingCurve,
} from './GradientGenerator.helpers';

interface Props {
  colors: Array<ColorWithId>;
  colorMode: ColorMode;
  precision: number;
  angle: number;
  easingCurve: EasingCurve;
  setColors: React.Dispatch<React.SetStateAction<Array<ColorWithId>>>;
  setColorMode: React.Dispatch<React.SetStateAction<ColorMode>>;
  setPrecision: React.Dispatch<React.SetStateAction<number>>;
  setAngle: React.Dispatch<React.SetStateAction<number>>;
  setEasingCurve: React.Dispatch<React.SetStateAction<EasingCurve>>;
}

function Controls({
  colors,
  colorMode,
  precision,
  angle,
  easingCurve,
  setColors,
  setColorMode,
  setPrecision,
  setAngle,
  setEasingCurve,
}: Props) {
  const id = React.useId();

  const [animateBezierChanges, setAnimateBezierChanges] =
    React.useState(false);

  return (
    <Wrapper>
      <ColorsWrapper>
        <ColorsControl
          id={`${id}-colors`}
          colors={colors}
          setColors={setColors}
        />
      </ColorsWrapper>
      <ModeControl
        id={`${id}-mode`}
        colorMode={colorMode}
        setColorMode={setColorMode}
      />
      <SliderControl
        id="precision"
        label="Precision"
        value={precision}
        setValue={setPrecision}
        min={MIN_PRECISION}
        max={MAX_PRECISION}
      />
      <Row>
        <Cell>
          <AngleControl
            id={`${id}-angle`}
            angle={angle}
            setAngle={setAngle}
          />
        </Cell>
        <Cell>
          <BezierControl
            id={`${id}-bezier`}
            label="Easing Curve"
            value={easingCurve}
            animateChanges={animateBezierChanges}
            setValue={(v: EasingCurve) => {
              setAnimateBezierChanges(false);
              setEasingCurve(v);
            }}
          />
          <BezierPresets>
            {CURVE_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                onClick={() => {
                  setAnimateBezierChanges(true);
                  setEasingCurve(preset.curve);
                }}
                data-is-active={checkCurveEquality(
                  easingCurve,
                  preset.curve
                )}
              >
                {preset.label}
              </Button>
            ))}
          </BezierPresets>
        </Cell>
      </Row>
    </Wrapper>
  );
}

function checkCurveEquality(
  curve1: EasingCurve,
  curve2: EasingCurve
) {
  return (
    curve1[0].x === curve2[0].x &&
    curve1[0].y === curve2[0].y &&
    curve1[1].x === curve2[1].x &&
    curve1[1].y === curve2[1].y
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  isolation: isolate;
`;

const ColorsWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
  padding-top: 8px;
`;

const Cell = styled.div`
  flex: 1;
  min-width: 155px;
`;

const BezierPresets = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 4px 0px;
  font-size: 0.875rem;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  border: 2px solid var(--color-gray-100);
  border-radius: 4px;
  color: white;

  &[data-is-active='true'] {
    background: var(--color-gray-100);
  }

  &:focus-visible {
    outline-color: white;
    outline-offset: 4px;
  }

  &:hover {
    border-color: var(--color-gray-300);
  }

  &:active {
    border-color: var(--color-gray-500);
  }
`;

export default Controls;
